import { map, mergeMap, take } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { OrganizationService } from 'services/organization/organization.service';
import { defer, forkJoin, Observable, of } from 'rxjs';
import { PublicRegistry } from 'services/organization/organization.interface';
import { PublicRegistryFilters } from './public-registry.constants';
import _get from 'lodash-es/get';
import { SmartForm } from '@certemy/ui-components';

@Injectable()
export class PublicRegistryResolverService {
  constructor(private organizationService: OrganizationService, private router: Router) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    id = '',
  ): Observable<[PublicRegistry.ResponseRowData, SmartForm.FieldOption[], string]> {
    const requestParams = new PublicRegistryFilters();
    const registryId = getRegistryId(route.params['registryId'], id);
    const preview = state.url.includes('/public-registry/preview');
    return defer(() =>
      preview
        ? this.organizationService.getPreviewOrganizationRegistry(registryId, requestParams)
        : this.organizationService.getPublicOrganizationRegistry(registryId, requestParams),
    ).pipe(
      take(1),
      map((data) => {
        if (!_get(data, 'result')) {
          this.router.navigate(['/not-found']);
          return null;
        } else {
          return data;
        }
      }),
      mergeMap((response: PublicRegistry.ResponseRowData) => {
        const boardList$ = response.organization.isParentRegistry
          ? this.organizationService.getBoardList(registryId)
          : of([]);
        return forkJoin([of(response), boardList$, of(registryId)]);
      }),
    );
  }
}

export const publicRegistryResolver: ResolveFn<
  Observable<[PublicRegistry.ResponseRowData, SmartForm.FieldOption[], string]>
> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(PublicRegistryResolverService).resolve(route, state);
};

@Injectable()
export class PublicRegistryRecordResolverService {
  constructor(private organizationService: OrganizationService, private router: Router) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    id = '',
  ): Observable<[PublicRegistry.ResponseRowData, string]> {
    const { numberInstanceId } = route.params;
    const registryId = getRegistryId(route.params['registryId'], id);
    const preview = state.url.includes('/public-registry/preview');
    return defer(() =>
      preview
        ? this.organizationService.getPreviewRegistryItemByNumberInstanceId(registryId, numberInstanceId)
        : this.organizationService.getPublicRegistryItemByNumberInstanceId(registryId, numberInstanceId),
    ).pipe(
      map((data) => {
        if (!data || !data.count) {
          this.router.navigate(['/not-found']);
          return null;
        } else {
          return [data, registryId];
        }
      }),
    );
  }
}

export const publicRegistryRecordResolver: ResolveFn<Observable<[PublicRegistry.ResponseRowData, string]>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(PublicRegistryRecordResolverService).resolve(route, state);
};

const getRegistryId = (registryId: string, id: string): string => {
  return id || registryId;
};
